/**
 * Редактор TG inline кнопок для шаблонов
 * Дата: 07.02.2026 - Создан для TODO-053 (Template Buttons ↔ Auto-Replies)
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/pages/Templates.tsx - используется в форме создания/редактирования шаблонов
 * - frontend/src/api/templates.ts - TemplateButton интерфейс
 * - internal/domain/entity/template.go - TemplateButton структура
 * 
 * КАК РАБОТАЕТ:
 * 1. Позволяет добавлять/удалять/редактировать TG inline кнопки
 * 2. Валидация: callback_data уникальный, не пустой, латиница+цифры+underscore
 * 3. Drag-n-drop для сортировки кнопок (опционально)
 * 4. Отображается только для шаблонов с messenger = 'telegram'
 * 
 * СХЕМА:
 * Templates.tsx → TemplateButtonsEditor → formData.buttons
 * TemplateButtonsEditor → onChange(buttons) → обновление formData
 */
import { useState } from 'react';
import { Button, Input } from '@/components/ui';
import { TemplateButton } from '@/api/templates';
import { Plus, Trash2, GripVertical } from 'lucide-react';

interface TemplateButtonsEditorProps {
  buttons: TemplateButton[];
  onChange: (buttons: TemplateButton[]) => void;
  testId?: string;
}

export function TemplateButtonsEditor({ buttons, onChange, testId }: TemplateButtonsEditorProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const addButton = () => {
    const newButton: TemplateButton = {
      text: '',
      callback_data: '',
      type: 'quick_reply',
    };
    onChange([...buttons, newButton]);
  };

  const removeButton = (index: number) => {
    onChange(buttons.filter((_, i) => i !== index));
  };

  const updateButton = (index: number, field: keyof TemplateButton, value: string) => {
    const updated = [...buttons];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const validateCallbackData = (callbackData: string): string | null => {
    if (!callbackData) {
      return 'Callback data обязателен';
    }
    if (!/^[a-zA-Z0-9_]+$/.test(callbackData)) {
      return 'Только латиница, цифры и underscore';
    }
    // Проверка уникальности
    const duplicates = buttons.filter((btn, idx) => 
      idx !== buttons.findIndex(b => b.callback_data === callbackData) && 
      btn.callback_data === callbackData
    );
    if (duplicates.length > 0) {
      return 'Callback data должен быть уникальным';
    }
    return null;
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    
    if (draggedIndex !== index) {
      const newButtons = [...buttons];
      const [removed] = newButtons.splice(draggedIndex, 1);
      newButtons.splice(index, 0, removed);
      onChange(newButtons);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4" data-testid={testId || 'template-buttons-editor'}>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-neutral-700">
          🔘 Inline кнопки для Telegram Bot
        </label>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={addButton}
          testId="btn-add-button"
        >
          <Plus className="w-4 h-4 mr-1" />
          Добавить кнопку
        </Button>
      </div>
      
      {buttons.length === 0 ? (
        <div className="text-sm text-neutral-500 p-4 border border-dashed rounded-lg text-center">
          Нет кнопок. Нажмите «Добавить кнопку» для создания inline кнопок.
        </div>
      ) : (
        <div className="space-y-3">
          {buttons.map((button, index) => {
            const callbackError = validateCallbackData(button.callback_data);
            return (
              <div
                key={index}
                className="border rounded-lg p-4 bg-neutral-50"
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                data-testid={`button-item-${index}`}
              >
                <div className="flex items-start gap-2">
                  <div className="cursor-move mt-2 text-neutral-400 hover:text-neutral-600">
                    <GripVertical className="w-5 h-5" />
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="Текст кнопки"
                        value={button.text}
                        onChange={(e) => updateButton(index, 'text', e.target.value)}
                        placeholder="👍 Отлично"
                        testId={`input-button-text-${index}`}
                      />
                      <div>
                        <Input
                          label="Callback Data *"
                          value={button.callback_data}
                          onChange={(e) => updateButton(index, 'callback_data', e.target.value)}
                          placeholder="feedback_good"
                          testId={`input-button-callback-${index}`}
                          className={callbackError ? 'border-red-500' : ''}
                        />
                        {callbackError && (
                          <p className="text-xs text-red-500 mt-1">{callbackError}</p>
                        )}
                        <p className="text-xs text-neutral-500 mt-1">
                          Только латиница, цифры и underscore. Используется в webhook.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name={`button-type-${index}`}
                          checked={button.type === 'quick_reply'}
                          onChange={() => updateButton(index, 'type', 'quick_reply')}
                          className="h-4 w-4"
                        />
                        <span>Quick Reply</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name={`button-type-${index}`}
                          checked={button.type === 'url'}
                          onChange={() => updateButton(index, 'type', 'url')}
                          className="h-4 w-4"
                        />
                        <span>URL</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name={`button-type-${index}`}
                          checked={button.type === 'phone_number'}
                          onChange={() => updateButton(index, 'type', 'phone_number')}
                          className="h-4 w-4"
                        />
                        <span>Phone Number</span>
                      </label>
                    </div>
                    {button.type === 'url' && (
                      <Input
                        label="URL"
                        value={button.url || ''}
                        onChange={(e) => updateButton(index, 'url', e.target.value)}
                        placeholder="https://example.com"
                        testId={`input-button-url-${index}`}
                      />
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => removeButton(index)}
                    testId={`btn-remove-button-${index}`}
                    className="mt-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      <p className="text-xs text-neutral-500">
        💡 Кнопки будут отображаться в Telegram Bot как inline keyboard. 
        При нажатии кнопки будет отправлен callback_query с указанным callback_data.
      </p>
    </div>
  );
}
